// frontend/src/components/AuditorPortalLayout.js
// Dedicated read-only portal layout for external auditors.
// Follows the same rendering pattern as ClientPortalLayout.

import React, { useState, useEffect, useMemo } from 'react';

// Sub-components (one per audit section)
import AuditorOverview from './auditor/AuditorOverview';
import AuditorFinancials from './auditor/AuditorFinancials';
import AuditorTrust from './auditor/AuditorTrust';
import AuditorTimeBilling from './auditor/AuditorTimeBilling';
import AuditorTax from './auditor/AuditorTax';
import AuditorCasesClients from './auditor/AuditorCasesClients';
import AuditorCompliance from './auditor/AuditorCompliance';
import AuditorActivityLog from './auditor/AuditorActivityLog';

// ─── Tab definitions ────────────────────────────────────────
const TABS = [
  { id: 'overview',    label: 'Overview',          icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { id: 'financials',  label: 'Financials',        icon: 'M4 7h16a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V9a2 2 0 012-2zm0 0V5a2 2 0 012-2h10a2 2 0 012 2v2M16 13h2' },
  { id: 'trust',       label: 'Trust Accounts',    icon: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.002 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.002 0M18 7l3 9m-3-9l-6-2' },
  { id: 'timeBilling', label: 'Time & Billing',    icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
  { id: 'tax',         label: 'Tax',               icon: 'M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z' },
  { id: 'caseClients', label: 'Cases & Clients',   icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { id: 'compliance',  label: 'Compliance',        icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
  { id: 'activityLog', label: 'Activity Log',      icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
];

// ─── Helper: SVG icon from path ──────────────────────────────
const TabIcon = ({ path, className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={path} />
  </svg>
);

// ─── Expiry countdown helper ─────────────────────────────────
function useCountdown(expiresAt) {
  const [remaining, setRemaining] = useState('');
  useEffect(() => {
    if (!expiresAt) return;
    const update = () => {
      const diff = new Date(expiresAt) - new Date();
      if (diff <= 0) { setRemaining('Expired'); return; }
      const days = Math.floor(diff / 86400000);
      const hrs = Math.floor((diff % 86400000) / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      setRemaining(`${days}d ${hrs}h ${mins}m`);
    };
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, [expiresAt]);
  return remaining;
}


export default function AuditorPortalLayout({
  currentUser,
  handleLogout,
  authAxios,
  accountExpiresAt,
  showFormMessage,
}) {
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setFullYear(start.getFullYear() - 1);
    return {
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
    };
  });

  const countdown = useCountdown(accountExpiresAt);

  // ── Firm name (already in currentUser from current-user endpoint) ──
  const firmName = currentUser?.firm_name || 'Firm';

  // Common props passed to every sub-component
  const sharedProps = useMemo(() => ({
    authAxios,
    dateRange,
    setDateRange,
    showFormMessage,
    firmName,
  }), [authAxios, dateRange, setDateRange, showFormMessage, firmName]);

  // ── Render active tab content ──────────────────────────────
  const renderContent = () => {
    switch (activeTab) {
      case 'overview':     return <AuditorOverview {...sharedProps} />;
      case 'financials':   return <AuditorFinancials {...sharedProps} />;
      case 'trust':        return <AuditorTrust {...sharedProps} />;
      case 'timeBilling':  return <AuditorTimeBilling {...sharedProps} />;
      case 'tax':          return <AuditorTax {...sharedProps} />;
      case 'caseClients':  return <AuditorCasesClients {...sharedProps} />;
      case 'compliance':   return <AuditorCompliance {...sharedProps} />;
      case 'activityLog':  return <AuditorActivityLog {...sharedProps} />;
      default:             return <AuditorOverview {...sharedProps} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ─── Top navbar ─────────────────────────────────────── */}
      <header className="bg-gradient-to-r from-slate-800 to-slate-700 text-white shadow-lg sticky top-0 z-50">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: branding + firm */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <svg className="w-7 h-7 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-lg font-bold tracking-tight">Audit Portal</span>
              </div>
              <span className="hidden sm:inline text-slate-400">|</span>
              <span className="hidden sm:inline text-slate-300 text-sm">{firmName}</span>
            </div>

            {/* Center: date range picker */}
            <div className="hidden md:flex items-center space-x-2 bg-slate-600/50 rounded-lg px-3 py-1.5">
              <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="bg-transparent text-sm text-white border-none focus:ring-0 p-0 w-32"
              />
              <span className="text-slate-400 text-sm">to</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="bg-transparent text-sm text-white border-none focus:ring-0 p-0 w-32"
              />
            </div>

            {/* Right: user info + expiry badge + logout */}
            <div className="flex items-center space-x-4">
              {countdown && countdown !== 'Expired' && (
                <div className="hidden sm:flex items-center space-x-1.5 bg-amber-500/20 border border-amber-500/30 rounded-full px-3 py-1">
                  <svg className="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-xs text-amber-300 font-medium">{countdown} remaining</span>
                </div>
              )}
              {countdown === 'Expired' && (
                <div className="flex items-center space-x-1.5 bg-red-500/20 border border-red-500/30 rounded-full px-3 py-1">
                  <span className="text-xs text-red-300 font-medium">Access expired</span>
                </div>
              )}
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium">{currentUser?.first_name || currentUser?.username}</p>
                <p className="text-xs text-slate-400">Auditor</p>
              </div>
              <div className="w-9 h-9 rounded-full bg-teal-500 flex items-center justify-center text-white font-bold text-sm">
                {(currentUser?.username || 'A').charAt(0).toUpperCase()}
              </div>
              <button
                onClick={handleLogout}
                className="p-2 rounded-lg hover:bg-slate-600 transition-colors"
                title="Log out"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>

          {/* ─── Tab navigation (horizontal) ──────────────── */}
          <nav className="flex overflow-x-auto scrollbar-hide -mb-px">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-teal-400 text-teal-400'
                    : 'border-transparent text-slate-400 hover:text-white hover:border-slate-500'
                }`}
              >
                <TabIcon path={tab.icon} className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* ─── Mobile date range (visible on small screens) ── */}
      <div className="md:hidden bg-white border-b px-4 py-2 flex items-center space-x-2">
        <span className="text-xs text-gray-500">Period:</span>
        <input
          type="date"
          value={dateRange.startDate}
          onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
          className="text-xs border rounded px-2 py-1 flex-1"
        />
        <span className="text-xs text-gray-400">to</span>
        <input
          type="date"
          value={dateRange.endDate}
          onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
          className="text-xs border rounded px-2 py-1 flex-1"
        />
      </div>

      {/* ─── Countdown banner (mobile) ─────────────────── */}
      {countdown && (
        <div className={`sm:hidden text-center py-1.5 text-xs font-medium ${
          countdown === 'Expired' ? 'bg-red-100 text-red-700' : 'bg-amber-50 text-amber-700'
        }`}>
          {countdown === 'Expired' ? 'Access expired' : `Access expires in ${countdown}`}
        </div>
      )}

      {/* ─── Main content area ─────────────────────────── */}
      <main className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {renderContent()}
      </main>

      {/* ─── Footer ────────────────────────────────────── */}
      <footer className="bg-slate-800 text-slate-500 text-center py-4 text-xs mt-8">
        Read-only audit access &middot; LegalOps ERP &middot; {new Date().getFullYear()}
      </footer>
    </div>
  );
}
